import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, BellRing, CheckCircle2, Loader2 } from 'lucide-react';
import {
  acknowledgeCriticalNotificationPublic,
  getCriticalNotificationPublicStatus,
  type CriticalNotificationPublicStatus,
} from '../services/criticalNotificationService';
import '../theme/index.css';
import './CriticalAcknowledgement.css';

function formatTimestamp(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isAcknowledged(status?: string): boolean {
  return (status || '').trim().toLowerCase() === 'acknowledged';
}

function LoadingState() {
  return (
    <div className="critical-ack-page__state">
      <Loader2
        className="critical-ack-page__spinner"
        aria-hidden="true"
      />
      <h1 className="critical-ack-page__title">Loading notification</h1>
      <p className="critical-ack-page__message">
        Please wait while we fetch the notification status.
      </p>
    </div>
  );
}

function ErrorState({ message }: Readonly<{ message: string }>) {
  return (
    <div className="critical-ack-page__state">
      <span className="critical-ack-page__icon critical-ack-page__icon--error">
        <AlertTriangle aria-hidden="true" />
      </span>
      <h1 className="critical-ack-page__title">Notification unavailable</h1>
      <p className="critical-ack-page__message">{message}</p>
    </div>
  );
}

function getAckMessage(acknowledged: boolean, justAcknowledged: boolean): string {
  if (!acknowledged) {
    return 'A critical result requires your acknowledgement. Please review and acknowledge below.';
  }
  if (justAcknowledged) {
    return 'Thank you. Your acknowledgement has been recorded.';
  }
  return 'This notification has already been acknowledged.';
}

function AcknowledgementContent({
  status,
  acknowledged,
  justAcknowledged,
  acknowledging,
  ackError,
  onAcknowledge,
}: Readonly<{
  status: CriticalNotificationPublicStatus;
  acknowledged: boolean;
  justAcknowledged: boolean;
  acknowledging: boolean;
  ackError: string | null;
  onAcknowledge: () => void;
}>) {
  return (
    <>
      <div className="critical-ack-page__state">
        <span
          className={`critical-ack-page__icon ${
            acknowledged ? 'critical-ack-page__icon--success' : 'critical-ack-page__icon--pending'
          }`}
        >
          {acknowledged ? <CheckCircle2 aria-hidden="true" /> : <BellRing aria-hidden="true" />}
        </span>
        <h1 className="critical-ack-page__title">
          {acknowledged ? 'Notification acknowledged' : 'Critical result notification'}
        </h1>
        <p className="critical-ack-page__message">
          {getAckMessage(acknowledged, justAcknowledged)}
        </p>
      </div>

      <dl className="critical-ack-page__details">
        <div className="critical-ack-page__row">
          <dt>Acknowledgement</dt>
          <dd>
            <span
              className={`critical-ack-page__badge ${
                acknowledged
                  ? 'critical-ack-page__badge--acknowledged'
                  : 'critical-ack-page__badge--pending'
              }`}
            >
              {status.acknowledgementStatus || 'Unknown'}
            </span>
          </dd>
        </div>
        {acknowledged ? (
          <div className="critical-ack-page__row">
            <dt>Acknowledged at</dt>
            <dd>{formatTimestamp(status.acknowledgedAt)}</dd>
          </div>
        ) : null}
      </dl>

      {!acknowledged ? (
        <div className="critical-ack-page__actions">
          <button
            type="button"
            className="critical-ack-page__ack-btn"
            disabled={acknowledging}
            onClick={onAcknowledge}
          >
            {acknowledging ? (
              <Loader2
                className="critical-ack-page__spinner"
                aria-hidden="true"
              />
            ) : (
              <CheckCircle2 aria-hidden="true" />
            )}
            {acknowledging ? 'Acknowledging…' : 'Acknowledge'}
          </button>
          {ackError ? (
            <p
              className="critical-ack-page__error"
              role="alert"
            >
              {ackError}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

const CriticalAcknowledgement: React.FC = () => {
  // Token is used as-is from the path — do not decodeURIComponent.
  const { token: rawToken } = useParams<{ token: string }>();
  const token = (rawToken || '').trim();
  const paramsValid = token.length > 0;

  const [status, setStatus] = useState<CriticalNotificationPublicStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);
  const [justAcknowledged, setJustAcknowledged] = useState(false);

  useEffect(() => {
    document.body.classList.add('critical-ack-page--active');
    return () => {
      document.body.classList.remove('critical-ack-page--active');
    };
  }, []);

  const loadStatus = useCallback(async (): Promise<boolean> => {
    const result = await getCriticalNotificationPublicStatus(token);
    if (!result.success || !result.data) {
      setError(result.error || 'Unable to load notification status.');
      setStatus(null);
      return false;
    }
    setError(null);
    setStatus(result.data);
    return true;
  }, [token]);

  useEffect(() => {
    if (!paramsValid) {
      setError('This acknowledgement link is invalid or incomplete.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await getCriticalNotificationPublicStatus(token);
      if (cancelled) {
        return;
      }
      if (!result.success || !result.data) {
        setError(result.error || 'Unable to load notification status.');
        setStatus(null);
      } else {
        setError(null);
        setStatus(result.data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [paramsValid, token]);

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    setAckError(null);
    const result = await acknowledgeCriticalNotificationPublic(token, 'IN_APP');
    if (!result.success) {
      setAckError(result.error || 'Failed to acknowledge notification.');
      setAcknowledging(false);
      return;
    }
    setJustAcknowledged(true);
    const refreshed = await loadStatus();
    if (!refreshed) {
      // Ack succeeded; keep success UI even if status refresh failed.
      setError(null);
      setStatus(prev =>
        prev
          ? {
              ...prev,
              acknowledgementStatus: 'Acknowledged',
              acknowledgedAt: prev.acknowledgedAt || new Date().toISOString(),
            }
          : {
              criticalNotificationId: 0,
              notificationStatus: '',
              acknowledgementStatus: 'Acknowledged',
              acknowledgedAt: new Date().toISOString(),
            }
      );
    }
    setAcknowledging(false);
  };

  const acknowledged = isAcknowledged(status?.acknowledgementStatus);

  let body: React.ReactNode;
  if (loading) {
    body = <LoadingState />;
  } else if (error || !status) {
    body = <ErrorState message={error || 'The requested notification could not be loaded.'} />;
  } else {
    body = (
      <AcknowledgementContent
        status={status}
        acknowledged={acknowledged}
        justAcknowledged={justAcknowledged}
        acknowledging={acknowledging}
        ackError={ackError}
        onAcknowledge={() => void handleAcknowledge()}
      />
    );
  }

  return (
    <div className="critical-ack-page">
      <div className="critical-ack-page__card">{body}</div>
    </div>
  );
};

export default CriticalAcknowledgement;
